import "../navigation.scss";

import { useAuthenticator } from "@aws-amplify/ui-react";
import { Drawer } from "@mui/material";
import { DocumentOutline } from "assets/img/svg/documentOutline";
import Folder from "assets/img/svg/folder";
import Dashboard from "assets/img/svg/navbar/dashboard";
import PersonalInfo from "assets/img/svg/navbar/personalInfo";
import Wallet from "assets/img/svg/navbar/wallet";
import PlusSquare from "assets/img/svg/plusSquare";
import classNames from "classnames";
import { getEmptyMandatoryCustomFields } from "components/Beneficiar/utils";
import InstallCrealoAppModal from "components/InstallCrealoAppModal";
import NotificationCenter from "components/NotificationCenter";
import type { TBeneficiarCustomField } from "containers/Beneficiars/types";
import { PortalType } from "contexts/portal.context";
import RenderIf from "helpers/RenderIf";
import { useBPCompanySettings } from "hooks/useBPCompanySettings";
import useCrisp from "hooks/useCrisp";
import { useIsMobile } from "hooks/useIsMobile";
import { useIsPWA } from "hooks/usePWAMode";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toggleNavDrawer, toggleNotificationsDrawer } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import { useAuth } from "../../../../contexts/auth/AuthContext";
import { displayMobileNavBtn } from "../../BPMobileNavBtn";
import RolePreview from "../../RolePreview";
import {
    HelpMenu,
    NavigationHeader,
    NavigationLayout,
    NavigationLink,
    NavigationProfile,
    NotificationsButton,
} from "../components";
import type { NavigationLinkConfig } from "../types";

/**
 * Navigation component for Beneficiary portal (Espace Auteur)
 */
const BeneficiaryNavigation = () => {
    const portalType = useContext(PortalType);
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { isMobile } = useIsMobile();
    const isPWAMode = useIsPWA();

    const onOpenNavDrawer = useAppSelector((state) => state.settings.showNavDrawer);
    const onOpenNotificationsDrawer = useAppSelector((state) => state.settings.showNotificationsDrawer);
    const { user, signOut, authStatus } = useAuthenticator((context) => [context.user]);

    const { notifications } = useAppSelector((state) => state.notification);
    const { rolePreview } = useAppSelector((state) => state.settings);
    const { beneficiarCompagnies, beneficiarPersonalInfo, missingData, isInvited } = useAppSelector(
        (state) => state.beneficiary,
    );
    const { customFields } = useAppSelector((state) => state.customFields);

    const [openInstallAppModal, setOpenInstallAppModal] = useState(false);
    const { isAdminCrealo: isCrealoAdmin } = useAuth();

    const { getBPCompanySettings } = useBPCompanySettings({ portalType: "beneficiary" });

    // Get company with avatar for display
    const company = beneficiarCompagnies.find((comp) => !!comp.avatarUrl);
    const mainBeneficiaryPortalType = beneficiarCompagnies[0]?.publisherType;
    const usesAgreementsOnly = getBPCompanySettings("uses_agreements_only", company?.id ?? -1);

    // Check for missing mandatory fields
    const emptyMandatoryCustomFields = useMemo(() => {
        return getEmptyMandatoryCustomFields(
            customFields,
            beneficiarPersonalInfo?.customFields as TBeneficiarCustomField[],
        );
    }, [beneficiarPersonalInfo, customFields]);

    const hasEmptyMandatoryInfo = isInvited && (!!emptyMandatoryCustomFields.length || !!missingData.length);
    const notificationsCount = notifications.filter((notif) => !notif.seen).length;

    // Initialize Crisp chat
    useCrisp(portalType);

    useEffect(() => {
        if (authStatus === "unauthenticated") window.location.reload();
    }, [authStatus]);

    const handleLogOut = () => {
        dispatch({ type: "USER/LOGOUT" });
        signOut();
    };

    const onCloseNavDrawer = () => {
        setTimeout(() => dispatch(toggleNavDrawer(false)), 100);
    };

    const onOpenNotifications = () => {
        if (isMobile) {
            displayMobileNavBtn(false);
            dispatch(toggleNotificationsDrawer(true));
        } else {
            dispatch(toggleNotificationsDrawer(!onOpenNotificationsDrawer));
        }
    };

    const onCloseNotifications = () => {
        if (isMobile) displayMobileNavBtn(true);
        dispatch(toggleNotificationsDrawer(false));
    };

    const onOpenInstallAppModal = () => {
        setOpenInstallAppModal(true);
        displayMobileNavBtn(false);
    };

    const onCloseInstallAppModal = () => {
        setOpenInstallAppModal(false);
        displayMobileNavBtn(true);
    };

    const blockerStyle = hasEmptyMandatoryInfo ? { opacity: 0.12, pointerEvents: "none" as const } : undefined;

    // Navigation links configuration
    const mainLinks: NavigationLinkConfig[] = [
        {
            to: "/dashboard",
            icon: <Dashboard />,
            label: "NavigationBar.Dashboard",
        },
        {
            to: "/titles",
            icon: <Wallet />,
            label: `NavigationBar.Titles_${mainBeneficiaryPortalType}`,
        },
    ];

    const statementsLink: NavigationLinkConfig = {
        to: "/statements",
        icon: <DocumentOutline />,
        label: "NavigationBar.Statements",
    };

    const documentsLink: NavigationLinkConfig = {
        to: "/documents",
        icon: <Folder />,
        label: "NavigationBar.Documents",
    };

    const settingsLink: NavigationLinkConfig = {
        to: "/settings",
        icon: <PersonalInfo />,
        label: "NavigationBar.PersonalInfo",
    };

    const topContent = (
        <>
            <NavigationHeader />
            <div className="navigation-main">
                {mainLinks.map((link) => (
                    <NavigationLink key={link.to} config={link} onClick={onCloseNavDrawer} />
                ))}

                <RenderIf show={!usesAgreementsOnly}>
                    <NavigationLink config={statementsLink} onClick={onCloseNavDrawer} />
                </RenderIf>

                <NavigationLink config={documentsLink} onClick={onCloseNavDrawer} />
            </div>
        </>
    );

    const bottomContent = (
        <>
            <NavigationLink config={settingsLink} onClick={onCloseNavDrawer} />

            <HelpMenu
                portalType="beneficiary"
                hasNavLinks={false}
                onActionComplete={isMobile ? () => dispatch(toggleNavDrawer(false)) : undefined}
            />

            <NotificationsButton unreadCount={notificationsCount} onClick={onOpenNotifications} />

            {isMobile && (
                <button className="navigation-link navigation-button" onClick={onOpenInstallAppModal}>
                    <div className="navigation-link__icon">
                        <PlusSquare />
                    </div>
                    {t("BPSettings.InstallCrealoApp")}
                </button>
            )}

            {rolePreview && isCrealoAdmin && <RolePreview role={rolePreview.role} label={rolePreview.label} />}

            <NavigationProfile
                userName={user.attributes?.name}
                avatarUrl={company?.avatarUrl}
                portalType="beneficiary"
                showCompanySwitcher={false}
                onLogout={handleLogOut}
            />
        </>
    );

    return (
        <>
            <NavigationLayout
                variant="beneficiary"
                isMobile={isMobile}
                isOpen={onOpenNavDrawer}
                isPWAMode={isPWAMode}
                topContent={topContent}
                bottomContent={bottomContent}
                blockedStyle={blockerStyle}
            />

            <Drawer
                anchor="bottom"
                open={onOpenNotificationsDrawer}
                className={classNames("notification-wrapper", { "is-PWA": isPWAMode })}
                onClick={onCloseNotifications}
            >
                <NotificationCenter close={onCloseNotifications} />
            </Drawer>

            <InstallCrealoAppModal open={openInstallAppModal} close={onCloseInstallAppModal} />
        </>
    );
};

export default BeneficiaryNavigation;
